// Affiliate Product Catalog
// Maps product names to Amazon affiliate URLs for auto-linking in blog content.
// The auto-linker matches ALL occurrences of each product name and wraps them
// in affiliate <a> tags.

import { amazonUrl } from '@/lib/affiliate'

export interface AffiliateCatalogEntry {
  /** Primary display name (used as the canonical match) */
  displayName: string
  /** Additional text patterns to match (case-insensitive). Include the displayName variants. */
  aliases: string[]
  /** Full Amazon affiliate URL */
  affiliateUrl: string
}

export const AFFILIATE_CATALOG: AffiliateCatalogEntry[] = [
  // --- Centipede-safe weed control (PAAPI-verified in-stock 2026-06-26) ---
  {
    displayName: 'Fertilome Centipede Weed & Feed 15-0-15',
    aliases: [
      'Fertilome Centipede Weed & Feed 15-0-15',
      'Fertilome Centipede Weed and Feed 15-0-15',
      'Ferti-Lome Centipede Weed & Feed',
      'Fertilome Centipede Weed & Feed',
      'Fertilome Centipede Weed and Feed',
    ],
    affiliateUrl: amazonUrl('B0CYJMR85M'),
  },
  {
    displayName: 'Southern Ag Atrazine',
    aliases: [
      'Southern Ag Atrazine St. Augustine & Centipede Weed Killer',
      'Southern Ag Atrazine Weed Killer',
      'Southern Ag Atrazine',
    ],
    affiliateUrl: amazonUrl('B0052MZBYQ'),
  },
  {
    displayName: 'Celsius WG Herbicide',
    // Aliases stay scoped to the branded forms. The bare word "Celsius" must
    // never be a pattern here: posts use it for temperature units, and the
    // auto-linker would wrap those.
    aliases: [
      'Celsius WG Herbicide',
      'Celsius WG',
      'Celsius herbicide',
    ],
    affiliateUrl: amazonUrl('B0BTN1DPMD'),
  },
  // --- Scotts Weed & Feed / Triple Action family ---
  {
    displayName: 'Scotts Turf Builder Weed and Feed 3',
    aliases: [
      'Scotts Turf Builder Weed and Feed 3',
      'Scotts Turf Builder Weed & Feed 3',
      'Scotts Turf Builder Weed and Feed',
      'Scotts Turf Builder Weed & Feed',
      'Scotts Weed and Feed',
      'Scotts Weed & Feed',
    ],
    affiliateUrl: amazonUrl('B00XJM8KMQ'),
  },
  {
    displayName: 'Scotts Turf Builder Triple Action',
    aliases: [
      'Scotts Turf Builder Triple Action',
      'Scotts Triple Action',
    ],
    affiliateUrl: amazonUrl('B09JVFT7N5'),
  },
  // NOTE: amzn.to/41z6UBt resolves to B09JVFT7N5 (regular Triple Action),
  // not a true Southern variant. Anchors mentioning "Southern Triple Action"
  // would have misled users. The regular Triple Action entry above already
  // covers any "Triple Action" mention; intentionally leaving this commented
  // so we don't auto-link the Southern alias to a wrong product.
  // {
  //   displayName: 'Scotts Turf Builder Southern Triple Action',
  //   aliases: ['Scotts Turf Builder Southern Triple Action', 'Scotts Southern Triple Action', 'Southern Triple Action'],
  //   affiliateUrl: amazonUrl('???'), // Find true Southern Triple Action ASIN
  // },
  {
    displayName: 'Scotts Turf Builder Bonus S Southern Weed & Feed',
    aliases: [
      'Scotts Turf Builder Bonus S Southern Weed & Feed',
      'Scotts Turf Builder Bonus S Southern Weed and Feed',
      'Scotts Turf Builder Bonus S',
      'Bonus S Southern Weed & Feed',
      'Bonus S Southern',
    ],
    affiliateUrl: amazonUrl('B00GPAGO6S'),
  },

  // --- Spreaders ---
  {
    displayName: 'Scotts Elite Spreader',
    aliases: [
      'Scotts Elite Spreader',
      'Scotts Elite',
    ],
    affiliateUrl: amazonUrl('B077JBGG4H'),
  },
  {
    displayName: 'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
    aliases: [
      'Scotts Turf Builder EdgeGuard Mini Broadcast Spreader',
      'Scotts EdgeGuard Mini Broadcast Spreader',
      'Scotts EdgeGuard DLX',
      'Scotts EdgeGuard Mini',
      'EdgeGuard DLX',
      'EdgeGuard Mini',
    ],
    affiliateUrl: amazonUrl('B002YPS1KK'),
  },
  // NOTE: "Scotts Classic Drop" intentionally NOT auto-linked. It is discontinued, and the
  // prior entry remapped it to the EdgeGuard Mini (a BROADCAST spreader) — an anchor/product
  // mismatch (drop-type anchor → broadcast product) that violates anchor-matches-product.
  // Per CLAUDE.md, discontinued products with no true ASIN stay unlinked. Removed 2026-06-19.
  {
    displayName: 'Scotts Wizz Spreader',
    aliases: [
      'Scotts Wizz Spreader',
      'Scotts Hand-Held Wizz',
      'Scotts Wizz',
    ],
    affiliateUrl: amazonUrl('B011HY5Q1K'),
  },

  // --- Fertilizers ---
  {
    displayName: 'The Andersons PGF Complete 16-4-8',
    aliases: [
      'The Andersons PGF Complete 16-4-8',
      'Andersons PGF Complete 16-4-8',
      'The Andersons PGF Complete',
      'Andersons PGF Complete',
      'PGF Complete 16-4-8',
      'PGF Complete',
    ],
    affiliateUrl: amazonUrl('B07SYLCKRH'),
  },
  {
    displayName: 'Simple Lawn Solutions Advanced 16-4-8',
    aliases: [
      'Simple Lawn Solutions Advanced 16-4-8',
      'Simple Lawn Solutions 16-4-8',
      'Simple Lawn Solutions Advanced',
    ],
    affiliateUrl: amazonUrl('B06XWFL84R'),
  },
  // --- Simple Lawn Solutions liquid line (PAAPI-verified in-stock 2026-06-27).
  // Aliases are brand-qualified on purpose: bare NPK strings like "15-0-15" or
  // "liquid iron" must NOT be matched here or they would hijack the Fertilome
  // 15-0-15 and PetraTools iron mentions elsewhere in the catalog/content. ---
  {
    displayName: 'Simple Lawn Solutions Maximum Green 28-0-0',
    aliases: [
      'Simple Lawn Solutions Maximum Green 28-0-0',
      'Simple Lawn Solutions Maximum Green & Growth',
      'Simple Lawn Solutions 28-0-0',
      'SLS 28-0-0',
      'Maximum Green & Growth 28-0-0',
      'Maximum Green 28-0-0',
    ],
    affiliateUrl: amazonUrl('B06X9Z9MQR'),
  },
  {
    displayName: 'Simple Lawn Solutions Superior 15-0-15',
    aliases: [
      'Simple Lawn Solutions Superior 15-0-15',
      'Simple Lawn Solutions 15-0-15',
      'SLS 15-0-15',
      'Superior Nitrogen 15-0-15',
      'Superior 15-0-15',
    ],
    affiliateUrl: amazonUrl('B06X9L1GW5'),
  },
  {
    displayName: 'Simple Lawn Solutions Ultimate 3-18-18',
    aliases: [
      'Simple Lawn Solutions Ultimate 3-18-18',
      'Simple Lawn Solutions 3-18-18',
      'SLS 3-18-18',
      'Ultimate 3-18-18',
    ],
    affiliateUrl: amazonUrl('B06XB32CVV'),
  },
  {
    displayName: 'Simple Lawn Solutions Liquid Iron',
    aliases: [
      'Simple Lawn Solutions Liquid Iron Darker Green',
      'Simple Lawn Solutions Liquid Iron',
      'Simple Lawn Solutions Darker Green',
      'SLS Liquid Iron',
    ],
    affiliateUrl: amazonUrl('B08Z4DTTJR'),
  },
  {
    displayName: 'Simple Lawn Solutions Lawn Energizer 6-0-0 with Iron',
    aliases: [
      'Simple Lawn Solutions Commercial Grade Lawn Fertilizer with Iron',
      'Simple Lawn Solutions Lawn Energizer',
      'SLS Lawn Energizer',
    ],
    affiliateUrl: amazonUrl('B01BI8K6S8'),
  },
  {
    displayName: 'Simple Lawn Solutions Organic Micronutrient Booster',
    aliases: [
      'Simple Lawn Solutions Organic Micronutrient Booster',
      'Simple Grow Solutions Organic Micronutrient Booster',
      'Simple Lawn Solutions Micronutrient Booster',
      'SLS Micronutrient Booster',
    ],
    affiliateUrl: amazonUrl('B01N19BYG1'),
  },
  {
    displayName: 'Simple Lawn Solutions Liquid Soil Loosener',
    aliases: [
      'Simple Lawn Solutions Liquid Soil Loosener',
      'Simple Lawn Solutions Soil Loosener',
      'SLS Liquid Soil Loosener',
      'SLS Soil Loosener',
    ],
    affiliateUrl: amazonUrl('B071VWCRBD'),
  },
  {
    displayName: 'Milorganite 6-4-0',
    aliases: [
      'Milorganite 6-4-0',
      'Milorganite Slow-Release',
      'Milorganite',
    ],
    // 2026-07-06: restored to a 32 lb bag (B0FGDNF55M, in stock $34.84) — the more
    // economical, correct size for these lawns — now that a 32 lb listing is
    // available again. Prior stopgap was the 5 lb (B000UOB1P4) after the original
    // 32 lb (B0DDHTJKCY) went OOS on 2026-06-19. This is the ASIN new posts
    // auto-linking "Milorganite" resolve to.
    affiliateUrl: amazonUrl('B0FGDNF55M'),
  },
  {
    displayName: 'Scotts Turf Builder Southern Lawn Food',
    aliases: [
      'Scotts Turf Builder Southern Lawn Food',
      'Scotts Southern Turf Builder',
      'Scotts Southern Lawn Food',
    ],
    affiliateUrl: amazonUrl('B000UGU59K'),
  },
  {
    displayName: 'Scotts Turf Builder Lawn Food',
    aliases: [
      'Scotts Turf Builder Lawn Food',
    ],
    affiliateUrl: amazonUrl('B005W1QXQW'),
  },
  // amzn.to/3Q9kuZN now resolves to B0DYWRC5NM (Pennington Full Season) —
  // the original UltraGreen 32-0-5 was discontinued on Amazon and the
  // shortlink got remapped, per CLAUDE.md. Renamed entry + aliases so
  // anchors stop saying "UltraGreen" when the link goes to Full Season.
  {
    displayName: 'Pennington Full Season Lawn Fertilizer 32-0-5',
    aliases: [
      'Pennington Full Season Lawn Fertilizer 32-0-5',
      'Pennington Full Season Lawn Fertilizer',
      'Pennington Full Season 32-0-5',
      'Pennington Full Season',
    ],
    affiliateUrl: amazonUrl('B0DYWRC5NM'),
  },
  {
    displayName: 'Scotts Turf Builder Starter Fertilizer for New Grass',
    aliases: [
      'Scotts Turf Builder Starter Fertilizer for New Grass',
      'Scotts Turf Builder Starter Fertilizer',
      'Scotts Starter Fertilizer',
    ],
    affiliateUrl: amazonUrl('B00DK49DVA'),
  },
  {
    displayName: 'Down to Earth Bio-Turf 8-3-5',
    aliases: [
      'Down to Earth Bio-Turf 8-3-5',
      'Down to Earth Bio-Turf',
    ],
    affiliateUrl: amazonUrl('B0BRKJJ89Z'),
  },

  // --- Disease & Pest Control ---
  {
    displayName: 'Scotts DiseaseEx Lawn Fungicide',
    aliases: [
      'Scotts DiseaseEx Lawn Fungicide',
      'Scotts DiseaseEx',
      'DiseaseEx',
    ],
    affiliateUrl: amazonUrl('B075R7BNXV'),
  },

  // --- Grass Seed ---
  {
    displayName: 'Scotts Turf Builder Grass Seed Bermudagrass',
    aliases: [
      'Scotts Turf Builder Grass Seed Bermudagrass',
      'Scotts Turf Builder Bermudagrass',
      'Scotts Bermudagrass Seed',
      'Scotts Bermuda Grass Seed',
    ],
    affiliateUrl: amazonUrl('B00D4AESXS'),
  },

  // --- Weed Prevention ---
  {
    displayName: 'Scotts Halts Crabgrass and Grassy Weed Preventer',
    aliases: [
      'Scotts Halts Crabgrass and Grassy Weed Preventer',
      'Scotts Halts Crabgrass Preventer',
      'Scotts Halts',
    ],
    affiliateUrl: amazonUrl('B08L6T3B4H'),
  },
]

/**
 * Build a sorted list of all matchable patterns from the catalog.
 * Sorted longest-first so longer product names match before shorter substrings.
 * Each entry maps a lowercase pattern to its catalog entry.
 */
export function buildMatchPatterns(): Array<{ pattern: string; entry: AffiliateCatalogEntry }> {
  const patterns: Array<{ pattern: string; entry: AffiliateCatalogEntry }> = []

  for (const entry of AFFILIATE_CATALOG) {
    for (const alias of entry.aliases) {
      patterns.push({ pattern: alias.toLowerCase(), entry })
    }
  }

  // Sort longest first to prevent partial matches
  patterns.sort((a, b) => b.pattern.length - a.pattern.length)

  return patterns
}

// Lazily-built, cached pattern list for single-name lookups (findAffiliateEntry).
let cachedPatterns: Array<{ pattern: string; entry: AffiliateCatalogEntry }> | null = null

/**
 * Resolve a single product NAME to its catalog entry, if any alias matches
 * (case-insensitive substring; longest alias wins, same as the blog auto-linker).
 * Returns null when the product isn't in the catalog — e.g. non-Amazon brands
 * like Lesco — so callers can leave it unlinked per the affiliate rules. Used to
 * turn AI-named treatment products (which carry no ASIN) into verified affiliate
 * links only when a curated catalog match exists.
 */
export function findAffiliateEntry(text: string): AffiliateCatalogEntry | null {
  if (!text) return null
  if (!cachedPatterns) cachedPatterns = buildMatchPatterns()
  const lower = text.toLowerCase()
  for (const { pattern, entry } of cachedPatterns) {
    if (lower.includes(pattern)) return entry
  }
  return null
}
